import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, ChevronsUpDown, Plus, Search, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { useSubjectList, useCreateSubject } from 'modules/subject';
import type { Subject } from 'modules/subject/types';
import { cn } from 'lib/utils';

export interface SubjectSelectProps {
  id?: string;
  value?: string;
  onChange: (subjectId: string) => void;
  subjects?: Subject[];
  placeholder?: string;
  searchPlaceholder?: string;
  showCount?: boolean;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
}

export function SubjectSelect({
  id,
  value = '',
  onChange,
  subjects: propSubjects,
  placeholder,
  searchPlaceholder,
  showCount = false,
  disabled = false,
  className,
  triggerClassName,
}: SubjectSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [isCreatingCustom, setIsCreatingCustom] = React.useState(false);
  const [customName, setCustomName] = React.useState('');

  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);

  const subjectListQuery = useSubjectList();
  const isLoading = subjectListQuery.isLoading;

  const createSubject = useCreateSubject();

  // Combine parent-provided subjects with fetched list (favoring fresh query cache)
  const allSubjects = React.useMemo(() => {
    const fetched = subjectListQuery.data ?? [];
    if (propSubjects && propSubjects.length > 0) {
      // Merge in any newly created subjects that may not yet be in propSubjects
      const propIds = new Set(propSubjects.map((s) => s.id));
      const newItems = fetched.filter((s) => !propIds.has(s.id));
      return [...propSubjects, ...newItems];
    }
    return fetched;
  }, [propSubjects, subjectListQuery.data]);

  const selected = allSubjects.find((s) => s.id === value);

  const trimmedQuery = query.trim();

  const filtered = React.useMemo(() => {
    if (!trimmedQuery) return allSubjects;
    const lower = trimmedQuery.toLowerCase();
    return allSubjects.filter((s) => s.name.toLowerCase().includes(lower));
  }, [allSubjects, trimmedQuery]);

  const exactMatch = allSubjects.some(
    (s) => s.name.trim().toLowerCase() === trimmedQuery.toLowerCase(),
  );

  const handleSelect = (subjectId: string) => {
    onChange(subjectId);
    setOpen(false);
    setQuery('');
    setIsCreatingCustom(false);
    setCustomName('');
  };

  const handleCreate = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed || createSubject.isPending) return;

    try {
      const created = await createSubject.mutateAsync({ name: trimmed });
      if (created?.id) {
        handleSelect(created.id);
      }
    } catch {
      // Toast notification is handled in useCreateSubject
    }
  };

  const displayLabel = selected
    ? showCount && selected.questionCount !== undefined
      ? t('subject_option_with_count', { name: selected.name, count: selected.questionCount })
      : selected.name
    : placeholder || t('select_subject', 'Select a subject');

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setPortalContainer(
            (triggerRef.current?.closest('[role="dialog"],[role="alertdialog"]') as HTMLElement) ??
              null,
          );
        }
        setOpen(next);
        if (!next) {
          setQuery('');
          setIsCreatingCustom(false);
          setCustomName('');
        }
      }}
    >
      <PopoverPrimitive.Trigger asChild>
        <button
          id={id}
          ref={triggerRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled || createSubject.isPending}
          className={cn(
            'flex min-h-9 h-auto w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-colors',
            triggerClassName,
            className,
          )}
        >
          <span
            className={cn('text-left break-words leading-snug', !selected && 'text-muted-foreground')}
            title={displayLabel}
          >
            {displayLabel}
          </span>
          <ChevronsUpDown className="ml-1.5 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal container={portalContainer}>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          className={cn(
            'z-50 w-[var(--radix-popover-trigger-width)] min-w-[18rem] sm:min-w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        >
          {/* Search input */}
          <div className="flex items-center border-b border-border px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (filtered.length === 1) {
                    handleSelect(filtered[0].id);
                  } else if (trimmedQuery && !exactMatch) {
                    handleCreate(trimmedQuery);
                  }
                }
              }}
              placeholder={searchPlaceholder || t('search', 'Search…')}
              aria-label={searchPlaceholder || t('search', 'Search…')}
              className="flex h-9 w-full bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground focus:outline-none focus-visible:!outline-none focus:ring-0"
            />
          </div>

          {/* Options list */}
          <div
            role="listbox"
            style={{
              maxHeight: 'min(16rem, calc(var(--radix-popper-available-height, 100vh) - 5rem))',
            }}
            className="overflow-y-auto p-1"
          >
            {isLoading && allSubjects.length === 0 ? (
              <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                {t('loading', 'Loading…')}
              </div>
            ) : filtered.length === 0 ? (
              <p className="py-4 text-center text-xs text-muted-foreground">
                {t('no_subjects_match', 'No subjects match')}
              </p>
            ) : (
              filtered.map((subject) => {
                const isSelected = value === subject.id;
                const label =
                  showCount && subject.questionCount !== undefined
                    ? t('subject_option_with_count', {
                        name: subject.name,
                        count: subject.questionCount,
                      })
                    : subject.name;

                return (
                  <button
                    key={subject.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(subject.id)}
                    className={cn(
                      'relative flex w-full cursor-pointer select-none items-start rounded-sm py-2 pl-8 pr-2.5 text-xs outline-none transition-colors hover:bg-accent hover:text-accent-foreground',
                      isSelected && 'bg-accent/50 font-medium text-foreground',
                    )}
                  >
                    <span className="absolute left-2 top-2 flex h-4 w-4 items-center justify-center">
                      {isSelected && <Check className="h-3.5 w-3.5 text-foreground" />}
                    </span>
                    <span className="text-left break-words whitespace-normal leading-snug w-full">{label}</span>
                  </button>
                );
              })
            )}
          </div>

          {/* Quick create action from search query */}
          {trimmedQuery && !exactMatch && (
            <div className="border-t border-border p-1 bg-muted/20">
              <button
                type="button"
                onClick={() => handleCreate(trimmedQuery)}
                disabled={createSubject.isPending}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs font-medium text-foreground hover:bg-accent transition-colors text-left"
              >
                {createSubject.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                ) : (
                  <Plus className="h-3.5 w-3.5 shrink-0" />
                )}
                <span className="text-left break-words whitespace-normal leading-snug">
                  {t('create_subject_prompt', { name: trimmedQuery })}
                </span>
              </button>
            </div>
          )}

          {/* Custom create row at bottom */}
          {!trimmedQuery && (
            <div className="border-t border-border p-1 bg-muted/10">
              {isCreatingCustom ? (
                <div className="flex items-center gap-1.5 p-1">
                  <input
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCreate(customName);
                      } else if (e.key === 'Escape') {
                        setIsCreatingCustom(false);
                        setCustomName('');
                      }
                    }}
                    placeholder={t('new_subject_placeholder', 'Enter subject name…')}
                    autoFocus
                    className="flex-1 h-7 rounded border border-input bg-background px-2 text-xs outline-none focus-visible:!outline-none focus:border-foreground/40"
                  />
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    disabled={!customName.trim() || createSubject.isPending}
                    onClick={() => handleCreate(customName)}
                  >
                    {createSubject.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      t('add', 'Add')
                    )}
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsCreatingCustom(false);
                      setCustomName('');
                    }}
                    className="p-1 text-xs text-muted-foreground hover:text-foreground"
                    title={t('cancel', 'Cancel')}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsCreatingCustom(true)}
                  className="flex w-full items-center gap-1.5 rounded-sm px-2 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t('create_new_subject', 'Create new subject')}
                </button>
              )}
            </div>
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export default SubjectSelect;
